import { Injectable } from '@angular/core';
import {
  BehaviorSubject,
  distinctUntilChanged,
  map,
  Observable,
  Subscription,
} from 'rxjs';
import { TimelineApi } from '../api/timeline.api';
import { timelineElementHttpToTimelineElement } from '../helpers/timeline.helper';
import {
  Feed,
  TimelineElement,
  TimelineHttpResponse,
} from '../models/timeline.model';
import { LikeService } from '../services/like.service';
import { AbstractFacade } from './abstract.facade';

const NUMBER_ELEMENT_PER_PAGE: number = 15;

export interface TimelineFeedState {
  feed: Feed;
  totallyLoaded: boolean;
  loadingStarted: boolean;
  loading: boolean;
}

@Injectable()
export class TimelineFeedFacade extends AbstractFacade {
  private state: TimelineFeedState = {
    feed: [],
    totallyLoaded: false,
    loadingStarted: false,
    loading: false,
  };
  public readonly store: BehaviorSubject<TimelineFeedState> =
    new BehaviorSubject<TimelineFeedState>(this.state);
  public readonly state$: Observable<TimelineFeedState> =
    this.store.asObservable();
  subscriptions: Subscription[] = [];

  public readonly feed$: Observable<Feed> = this.state$.pipe(
    map((state: TimelineFeedState) => state.feed),
    distinctUntilChanged()
  );

  constructor(
    private readonly timelineApi: TimelineApi,
    private readonly likeService: LikeService
  ) {
    super();
  }

  getState(): TimelineFeedState {
    return this.state;
  }

  private setState(newState: Partial<TimelineFeedState>): void {
    this.state = { ...this.state, ...newState };
    this.store.next(this.state);
  }

  reset(): void {
    this.setState({
      feed: [],
      totallyLoaded: false,
      loadingStarted: false,
      loading: false,
    });
    this.subscriptions.forEach((subscription: Subscription) =>
      subscription.unsubscribe()
    );
  }

  loadFeed(page: number): void {
    if (this.state.totallyLoaded) {
      return;
    }
    this.setState({
      loadingStarted: true,
      loading: true,
    });
    this.subscriptions.push(
      this.timelineApi
        .getTimelineFeed(page, NUMBER_ELEMENT_PER_PAGE)
        .subscribe({
          next: (timelineHttpResponse: TimelineHttpResponse): void => {
            const lastAvailablePage: number =
              timelineHttpResponse?.meta?.last_page;
            const isFeedTotallyLoaded: boolean =
              lastAvailablePage != null && page >= lastAvailablePage;
            const realPageNumber: number = this.fixPageNumber(page);
            const paginatedFeed: TimelineElement[] =
              this.extractFeedFromTimelineHttpResponse(timelineHttpResponse);
            const feed: Feed = this.computeNewFeed(
              paginatedFeed,
              realPageNumber
            );
            this.setState({
              feed,
              totallyLoaded: isFeedTotallyLoaded,
              loading: false,
            });
          },
          error: (): void => {
            this.setState({
              loadingStarted: false,
              loading: false,
              totallyLoaded: true,
            });
          },
        })
    );
  }

  private fixPageNumber(pageNumber: number): number {
    return pageNumber <= 0 ? 1 : pageNumber;
  }

  private extractFeedFromTimelineHttpResponse(
    timelineHttpResponse: TimelineHttpResponse
  ): Feed {
    const feed: Feed = timelineHttpResponse.data.map(
      timelineElementHttpToTimelineElement
    );
    return feed;
  }

  private computeNewFeed(elements: Feed, page: number): Feed {
    const newFeed: Feed = [...this.state.feed];

    const pageIndex: number = page - 1;
    const indexOffset: number = pageIndex * NUMBER_ELEMENT_PER_PAGE;
    for (let i = 0; i < elements.length; ++i) {
      newFeed[i + indexOffset] = elements[i];
    }
    return newFeed;
  }

  isFeedTotallyLoaded(): boolean {
    return this.state.totallyLoaded;
  }

  likeElement(elementId: number) {
    const feed: Feed = this.state.feed;
    const element: TimelineElement = feed.find(
      (feedElem: TimelineElement) => feedElem?.id === elementId
    );
    if (!element || element.has_liked) {
      return;
    }
    element.has_liked = true;
    element.likes++;
    // Optimistic Assumption - Estimate that the application will be successful
    this.setState({
      feed: [...feed],
    });
    this.subscriptions.push(
      this.likeService.likeElement(element).subscribe({
        error: this.undoLikeOnError.bind(this, elementId),
      })
    );
  }

  undoLikeOnError(elementId: number): void {
    const feed: Feed = this.state.feed;
    const element: TimelineElement = feed.find(
      (feedElem: TimelineElement) => feedElem?.id === elementId
    );
    if (!element || !element.has_liked) {
      return;
    }
    element.has_liked = false;
    element.likes--;
    this.setState({
      feed: [...feed],
    });
  }

  unlikeElement(elementId: number): void {
    const feed: Feed = this.state.feed;
    const element: TimelineElement = feed.find(
      (feedElem: TimelineElement) => feedElem?.id === elementId
    );
    if (!element || !element.has_liked) {
      return;
    }
    // Optimistic Assumption - Estimate that the application will be successful
    element.has_liked = false;
    element.likes--;
    this.setState({
      feed: [...feed],
    });
    this.subscriptions.push(
      this.likeService.unLikeElement(element).subscribe({
        error: this.undoUnlikeOnError.bind(this, elementId),
      })
    );
  }

  undoUnlikeOnError(elementId: number): void {
    const feed: Feed = this.state.feed;
    const element: TimelineElement = feed.find(
      (feedElem: TimelineElement) => feedElem?.id === elementId
    );
    if (!element || element.has_liked) {
      return;
    }
    element.has_liked = true;
    element.likes++;
    this.setState({
      feed: [...feed],
    });
  }

  /**
   * Update comment number if it's needed and returns true if elementId is found
   */
  updateCommentsNumber(
    elementId: number,
    nbComments: number,
    hasCommented: boolean | null = null
  ): boolean {
    const feed: Feed = this.state.feed;
    const element: TimelineElement = feed.find(
      (feedElem: TimelineElement) => feedElem?.id === elementId
    );
    if (!element) {
      return false;
    }
    if (element.comments === nbComments) {
      return true;
    }
    if (element.comments < nbComments) {
      element.comments++;
    } else if (element.comments > nbComments) {
      element.comments--;
    }
    if (hasCommented !== null) {
      element.has_commented = hasCommented;
    }
    this.setState({
      feed: [...feed],
    });
    return true;
  }
}
